import { Stack, Typography } from '@mui/material';
import { first } from 'lodash';
import { useState } from 'react';

import { Building07Icon } from '@/components/icons/Building07Icon';
import { FileShield02Icon } from '@/components/icons/FileShield02Icon';
import { User01Icon } from '@/components/icons/User01Icon';
import { DialogModal } from '@/components/modals/DialogModal/DialogModal';
import { InfoTooltip } from '@/components/poppers/Tooltip/InfoTooltip';
import { ClientOrganizationModal } from '@/modules/clientOrganizations/ClientOrganizationModal';
import { ClientProfileSideDrawer } from '@/modules/clientProfiles/ClientProfileForm/ClientProfileModal';
import { AddNewAnythingActionCard } from '@/modules/common/AddNewAnythingModal/AddNewAnythingActionCard';
import { useDPFormRecipientTrackerContext } from '@/modules/dispositiveProvisions/contexts/DPFormRecipientTracker.context';
import { TestamentaryEntityModal } from '@/modules/entities/testamentaryEntities/TestamentaryEntityModal';
import { UnreachableError } from '@/utils/errors';

import { EntityDocumentViewerDocument } from '../documents/components/HouseholdDocumentsViewer/graphql/EntityDocumentViewer.generated';
import { CreateEntityShortFormModal } from '../entities/EntityShortFormModal/CreateEntityShortFormModal';
import {
  DispositiveProvisionsRecipientTemplateRecipientConfiguration,
  DispositiveProvisionsRecipientTypes,
} from './DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/DispositiveProvisionsRecipient.types';
import { GetDispositiveProvisionsDocument } from './DispositiveProvisionsForm/graphql/GetDispositiveProvisions.generated';
import { useDispositiveProvisionsTemplateSplitScreenModalContext } from './DispositiveProvisionsTemplateSplitScreenModal/DispositiveProvisionsTemplateSplitScreenModal.context';

// Queries that should not be refetched when creating a new recipient from within
// the DP workflow. Refetching GetDispositiveProvisions (which uses fetchPolicy: 'no-cache')
// during person/org/entity creation causes unnecessary loading state churn in the
// open DP form and can lead to the workflow closing unexpectedly.
const IGNORED_QUERY_DOCUMENTS = [
  EntityDocumentViewerDocument,
  GetDispositiveProvisionsDocument,
];

interface RecipientSelectorModalProps {
  handleClose: () => void;
  isOpen: boolean;
  householdId: string;
  deadPrimaryClientId: string | null;
  deadPrimaryClientIds: string[];
  isTwoClientHousehold: boolean;
  recipientConfiguration: DispositiveProvisionsRecipientTemplateRecipientConfiguration;
  /**
   * Called with the id of a recipient created from this modal (individual,
   * organization, sub-trust, or entity). Consumers use it to auto-select the
   * newly created recipient into the row that opened the modal — the create
   * mutations already refetch the possible-recipients list, so by the time this
   * fires the new id is available as an option.
   */
  onRecipientCreated?: (newRecipientId: string) => void;
}

export function TestamentaryEntitiesCardDescription() {
  return (
    <Stack
      spacing={0.5}
      direction="row"
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography display="flex" variant="body-xs">
        Sub-trust or inherited account
      </Typography>
      <InfoTooltip
        title={`Entities that will be created upon the death of the client, such as a marital trust or inherited retirement account.`}
        placement="top-start"
      />
    </Stack>
  );
}

export function RecipientSelectorModal({
  handleClose,
  isOpen,
  householdId,
  deadPrimaryClientId,
  deadPrimaryClientIds,
  isTwoClientHousehold,
  recipientConfiguration,
  onRecipientCreated,
}: RecipientSelectorModalProps) {
  const [isAddingIndividual, setIsAddingIndividual] = useState(false);
  const [isAddingTestamentaryEntity, setIsAddingTestamentaryEntity] =
    useState(false);
  const [isAddingOrganization, setIsAddingOrganization] = useState(false);
  const [isAddingEntity, setIsAddingEntity] = useState(false);
  const { isTemplateMode } =
    useDispositiveProvisionsTemplateSplitScreenModalContext();
  const { handleNewlyAddedRecipient } = useDPFormRecipientTrackerContext();

  const {
    createRecipientTypes,
    createEntityType,
    forceEntityType,
    limitEntityTypes,
  } = recipientConfiguration;

  const handleAdd = (
    typeToAdd: 'individual' | 'organization' | 'testamentaryEntity' | 'entity'
  ) => {
    handleClose();
    if (typeToAdd === 'individual') {
      setIsAddingIndividual(true);
    } else if (typeToAdd === 'testamentaryEntity') {
      setIsAddingTestamentaryEntity(true);
    } else if (typeToAdd === 'organization') {
      setIsAddingOrganization(true);
    } else if (typeToAdd === 'entity') {
      setIsAddingEntity(true);
    } else {
      throw new UnreachableError({
        case: typeToAdd,
        message: `Unexpected recipient type: ${typeToAdd}`,
      });
    }
  };

  // in template mode, if there's only a single recipient type, open the corresponding modal directly
  const firstCreateRecipientType = first(createRecipientTypes);
  if (
    isOpen &&
    firstCreateRecipientType &&
    createRecipientTypes.length === 1 &&
    isTemplateMode
  ) {
    switch (firstCreateRecipientType) {
      case DispositiveProvisionsRecipientTypes.Individual:
        return (
          <ClientProfileSideDrawer
            setIsOpen={(newIsOpen) => {
              if (newIsOpen) return;
              handleClose();
            }}
            isOpen={isOpen}
            clientProfileId={null}
            householdId={householdId}
            clientProfileTypeDisplay="individual"
            onAfterCreate={onRecipientCreated}
            ignoredQueryDocuments={IGNORED_QUERY_DOCUMENTS}
          />
        );
      case DispositiveProvisionsRecipientTypes.TestamentaryEntity:
        return (
          <TestamentaryEntityModal
            isOpen={isOpen}
            onClose={handleClose}
            dyingPrimaryClientId={deadPrimaryClientId}
            deadClientIds={deadPrimaryClientIds}
            isTwoClientHousehold={isTwoClientHousehold}
            ignoredQueryDocuments={IGNORED_QUERY_DOCUMENTS}
            defaultEstateTaxStatus={
              recipientConfiguration.testamentaryEntityTaxSettings
            }
            defaultName={
              recipientConfiguration.defaultNewTestamentaryEntityName
            }
            defaultKind={
              recipientConfiguration.defaultNewTestamentaryEntityKind
            }
            onAfterCreate={(created) => onRecipientCreated?.(created.id)}
          />
        );
      case DispositiveProvisionsRecipientTypes.Entity:
        return (
          <CreateEntityShortFormModal
            householdId={householdId}
            isOpen={isOpen}
            onClose={handleClose}
            initialEntityType={createEntityType}
            forceEntityType={forceEntityType}
            initialEntityName={recipientConfiguration.defaultNewEntityName}
            onAfterCreate={(createdEntityId) =>
              onRecipientCreated?.(createdEntityId)
            }
            ignoredQueryDocuments={IGNORED_QUERY_DOCUMENTS}
          />
        );
      case DispositiveProvisionsRecipientTypes.Organization:
      default:
        break;
    }
  }

  return (
    <>
      {setIsAddingIndividual && (
        <ClientProfileSideDrawer
          setIsOpen={setIsAddingIndividual}
          isOpen={isAddingIndividual}
          clientProfileId={null}
          householdId={householdId}
          clientProfileTypeDisplay="individual"
          onAfterCreate={onRecipientCreated}
          ignoredQueryDocuments={IGNORED_QUERY_DOCUMENTS}
        />
      )}
      {isAddingTestamentaryEntity && (
        <TestamentaryEntityModal
          isOpen={isAddingTestamentaryEntity}
          onClose={() => setIsAddingTestamentaryEntity(false)}
          dyingPrimaryClientId={deadPrimaryClientId}
          deadClientIds={deadPrimaryClientIds}
          isTwoClientHousehold={isTwoClientHousehold}
          ignoredQueryDocuments={IGNORED_QUERY_DOCUMENTS}
          defaultEstateTaxStatus={
            recipientConfiguration.testamentaryEntityTaxSettings
          }
          onAfterCreate={(created) => {
            handleNewlyAddedRecipient(created);
            onRecipientCreated?.(created.id);
          }}
        />
      )}
      {isAddingOrganization && (
        <ClientOrganizationModal
          householdId={householdId}
          setIsOpen={setIsAddingOrganization}
          isOpen={isAddingOrganization}
          organizationId={null}
          onAfterCreate={onRecipientCreated}
          ignoredQueryDocuments={IGNORED_QUERY_DOCUMENTS}
        />
      )}
      {isAddingEntity && (
        <CreateEntityShortFormModal
          householdId={householdId}
          isOpen={isAddingEntity}
          onClose={() => setIsAddingEntity(false)}
          initialEntityType={createEntityType}
          limitEntityTypes={limitEntityTypes}
          forceEntityType={forceEntityType}
          onAfterCreate={(createdEntityId) =>
            onRecipientCreated?.(createdEntityId)
          }
          ignoredQueryDocuments={IGNORED_QUERY_DOCUMENTS}
        />
      )}
      <DialogModal
        isOpen={isOpen}
        onClose={handleClose}
        heading="What would you like to add?"
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            {createRecipientTypes.includes(
              DispositiveProvisionsRecipientTypes.Individual
            ) && (
              <AddNewAnythingActionCard
                description="Individual"
                Icon={User01Icon}
                onClick={() => handleAdd('individual')}
              />
            )}

            {createRecipientTypes.includes(
              DispositiveProvisionsRecipientTypes.Organization
            ) && (
              <AddNewAnythingActionCard
                description="Organization"
                Icon={Building07Icon}
                onClick={() => handleAdd('organization')}
              />
            )}
            {createRecipientTypes.includes(
              DispositiveProvisionsRecipientTypes.Entity
            ) && (
              <AddNewAnythingActionCard
                description="Entity"
                Icon={FileShield02Icon}
                onClick={() => handleAdd('entity')}
              />
            )}
            {createRecipientTypes.includes(
              DispositiveProvisionsRecipientTypes.TestamentaryEntity
            ) && (
              <AddNewAnythingActionCard
                description={<TestamentaryEntitiesCardDescription />}
                Icon={FileShield02Icon}
                onClick={() => handleAdd('testamentaryEntity')}
              />
            )}
          </Stack>
        </Stack>
      </DialogModal>
    </>
  );
}
